import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { MarketingLayout } from "@/components/Layout/MarketingLayout";
import { LogoMark } from "@/components/Logo";
import { useLang } from "@/contexts/LanguageContext";
import { useAuth } from "@/contexts/AuthContext";
import { CustomerAuthModal } from "@/components/CustomerAuthModal";
import { ShieldCheck, Headphones, Percent } from "lucide-react";

/**
 * ServeOn — 3D tile home layout.
 * Big centered logo → 6 neon-glow tiles (3×2) → trust strip.
 * Tapping a tile opens the customer registration panel when logged out.
 */
export default function Landing() {
  const { lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [authOpen, setAuthOpen] = useState(false);
  const [pendingService, setPendingService] = useState(null);
  const [modStatus, setModStatus] = useState(null); // {key: 'live'|'off'}
  const isRTL = lang === "ar";

  useEffect(() => {
    api.get("/service-modules")
      .then((r) => {
        const map = {};
        (Array.isArray(r.data) ? r.data : []).forEach((m) => { if (m?.key) map[m.key] = m.status; });
        setModStatus(map);
      })
      .catch(() => setModStatus({}));
  }, []);

  const goToService = (key) => navigate(key ? `/app/service/${key}` : "/app");

  const handleTile = (tile) => {
    if (user && user.role === "customer") {
      goToService(tile.key);
    } else if (user) {
      navigate("/app");
    } else {
      setPendingService(tile.key);
      setAuthOpen(true);
    }
  };

  // Custom 3D-rendered tile icons (generated to match user mockup, stored in /public/tiles)
  const TILES = [
    {
      key: "restaurants",
      title_en: "Restaurants",
      title_ar: "المطاعم",
      bg: "linear-gradient(180deg, #7C2D12 0%, #451A03 100%)",
      glow: "#F97316",
      img: "/tiles/restaurants.png",
      route: "/register?service=restaurants",
    },
    {
      key: "flats",
      title_en: "Flats & Apartments",
      title_ar: "شقق ومساكن",
      bg: "linear-gradient(180deg, #78350F 0%, #422006 100%)",
      glow: "#EAB308",
      img: "/tiles/flats.png",
      route: "/register?service=flats",
    },
    {
      key: "home_services",
      title_en: "Home Service",
      title_ar: "خدمات منزلية",
      bg: "linear-gradient(180deg, #1E3A8A 0%, #0C1A44 100%)",
      glow: "#3B82F6",
      img: "/tiles/home_service.png",
      route: "/register?service=home_services",
    },
    {
      key: "grocery",
      title_en: "Grocery",
      title_ar: "البقالة",
      bg: "linear-gradient(180deg, #14532D 0%, #052E16 100%)",
      glow: "#22C55E",
      img: "/tiles/grocery.png",
      route: "/register?service=grocery",
    },
    {
      key: "hotels",
      title_en: "Hotels",
      title_ar: "الفنادق",
      bg: "linear-gradient(180deg, #831843 0%, #4A0E28 100%)",
      glow: "#EC4899",
      img: "/tiles/hotels.png",
      route: "/register?service=hotels",
    },
    {
      key: "pharmacy",
      title_en: "Pharmacy",
      title_ar: "الصيدلية",
      bg: "linear-gradient(180deg, #134E4A 0%, #04211F 100%)",
      glow: "#14B8A6",
      img: "/tiles/pharmacy.png",
      route: "/register?service=pharmacy",
    },
    {
      key: "ride_hailing",
      title_en: "Serv Wasel",
      title_ar: "سيرف واصل",
      title_bilingual: true,
      bg: "linear-gradient(180deg, #4C1D95 0%, #1E0A47 100%)",
      glow: "#8B5CF6",
      img: "/tiles/ride_hailing.png",
      route: "/register?service=ride_hailing",
    },
  ];

  return (
    <MarketingLayout>
      <div className="so-landing-bg min-h-[calc(100vh-3.5rem)]">
      <div
        className="max-w-md mx-auto md:max-w-2xl px-3 pt-3 pb-8"
        dir={isRTL ? "rtl" : "ltr"}
        data-testid="landing-page"
      >
        {/* ───── BIG CENTERED LOGO ───── */}
        <div
          className="flex flex-col items-center justify-center mb-5 md:mb-6"
          data-testid="hero-logo"
        >
          <div className="inline-flex items-center gap-3">
            <LogoMark size={56} />
            <span className="font-black text-4xl md:text-5xl tracking-tight leading-none">
              <span style={{ color: "var(--so-text)" }}>Serve</span>
              <span style={{ color: "var(--so-orange)" }}>On</span>
            </span>
          </div>
        </div>

        {/* ───── SERVICE TILE GRID (3×2, professional muted surfaces) ───── */}
        <div className="grid grid-cols-3 gap-4 mb-6" data-testid="service-tiles">
          {TILES.filter((tile) => !modStatus || modStatus[tile.key] !== "off").map((tile) => (
            <div key={tile.key} className="col-span-1">
                  <button
                    type="button"
                    onClick={() => handleTile(tile)}
                    data-testid={`tile-${tile.key}`}
                    className={`so-tile so-tile-${tile.key} w-full block rounded-2xl overflow-hidden relative group active:scale-[0.98]`}
                    style={{ aspectRatio: "3 / 4" }}
                  >
                    {/* 3D Illustration (top ~68%) */}
                    <div className="relative h-[68%] overflow-hidden flex items-center justify-center p-3 pt-4">
                      <img
                        src={tile.img}
                        alt={tile.title_en}
                        loading="lazy"
                        className="max-w-full max-h-full object-contain"
                        style={{ filter: "drop-shadow(0 6px 10px rgba(0,0,0,0.18))" }}
                      />
                    </div>

                    {/* Title block (bottom ~32%) */}
                    <div className="absolute inset-x-0 bottom-0 h-[32%] flex flex-col items-center justify-center gap-1.5 px-2 text-center" style={{ color: "var(--so-text)" }}>
                      {tile.title_bilingual ? (
                        <div className="font-bold text-[12px] md:text-sm leading-tight flex items-center gap-1">
                          <span>Serv</span>
                          <span
                            style={{ fontFamily: '"Noto Sans Arabic", sans-serif', color: "var(--tile-accent)" }}
                            dir="rtl"
                          >
                            واصل
                          </span>
                        </div>
                      ) : (
                        <div className="font-bold text-[12px] md:text-sm leading-tight">
                          {isRTL ? tile.title_ar : tile.title_en}
                        </div>
                      )}
                      {/* accent bar under title */}
                      <div className="h-[3px] w-8 rounded-full" style={{ background: "var(--tile-accent)" }} />
                    </div>
                  </button>
            </div>
          ))}
        </div>

        {/* ───── TRUST STRIP ───── */}
        <div
          className="rounded-3xl bg-[var(--so-surface)] border border-[var(--so-border)] p-4 md:p-5 mb-6"
          data-testid="trust-strip"
        >
          <div className="grid grid-cols-3 gap-2 md:gap-4">
            <TrustBadge
              icon={<Percent className="w-5 h-5 md:w-6 md:h-6" />}
              iconColor="#F97316"
              label={isRTL ? "أفضل قيمة" : "Best Value"}
            />
            <TrustBadge
              icon={<ShieldCheck className="w-5 h-5 md:w-6 md:h-6" />}
              iconColor="#06B6D4"
              label={isRTL ? "آمن وموثوق" : "Secure & Safe"}
            />
            <TrustBadge
              icon={<Headphones className="w-5 h-5 md:w-6 md:h-6" />}
              iconColor="#F97316"
              label={isRTL ? "دعم 24/7" : "24/7 Support"}
            />
          </div>
        </div>

      </div>
      </div>

      <CustomerAuthModal
        open={authOpen}
        onOpenChange={setAuthOpen}
        onSuccess={() => goToService(pendingService)}
      />
    </MarketingLayout>
  );
}

const TrustBadge = ({ icon, label }) => (
  <div className="flex flex-col items-center gap-1.5 text-center">
    <div
      className="w-10 h-10 md:w-11 md:h-11 rounded-full flex items-center justify-center border"
      style={{ background: "var(--so-bg)", borderColor: "var(--so-border)", color: "var(--so-text-soft)" }}
    >
      {icon}
    </div>
    <div className="font-semibold text-xs md:text-sm leading-tight" style={{ color: "var(--so-text-soft)" }}>{label}</div>
  </div>
);
